import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination, Navigation, EffectFade } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import "swiper/css/effect-fade";
import { api } from "@/lib/api";
import DestinationCard from "@/components/site/DestinationCard";
import DealStrip from "@/components/site/DealStrip";
import { Star, Search, Compass, ShieldCheck, HeadphonesIcon, Award, Users } from "lucide-react";
import { motion } from "framer-motion";

const HERO_IMG = "https://images.pexels.com/photos/23696832/pexels-photo-23696832.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600";

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [all, setAll] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({ happy_travellers: 5000, tours_completed: 500, destinations: 16, support: "24×7" });
  const [q, setQ] = useState("");
  const [tripType, setTripType] = useState("all");
  const [travelDate, setTravelDate] = useState("");
  const [pax, setPax] = useState(2);

  useEffect(() => {
    api.get("/packages", { params: { featured: true }}).then(r => setFeatured(r.data.packages));
    api.get("/packages").then(r => setAll(r.data.packages));
    api.get("/reviews").then(r => setReviews(r.data.reviews));
    api.get("/stats").then(r => setStats(r.data));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[640px] overflow-hidden">
        <img src={HERO_IMG} alt="Luxury travel" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative z-10 h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex items-center">
          <div className="max-w-3xl">
            <motion.div
              initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:.8}}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-[#D4AF37] text-xs uppercase tracking-[0.35em] font-semibold">
              <Compass className="w-3.5 h-3.5"/> Pack &amp; Go
            </motion.div>
            <motion.h1
              initial={{opacity:0, y:30}} animate={{opacity:1, y:0}} transition={{duration:.9, delay:.1}}
              data-testid="hero-title"
              className="mt-6 text-white text-5xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] tracking-tight">
              <span className="block">Your Trips.</span>
              <span className="block italic text-[#F5D67A]">Extraordinary journeys,</span>
              <span className="block">effortlessly crafted.</span>
            </motion.h1>
            <motion.p
              initial={{opacity:0}} animate={{opacity:1}} transition={{delay:.6}}
              className="mt-6 text-white/80 text-lg max-w-xl">
              Explore India &amp; international destinations with handpicked stays, private guides, and unforgettable moments — from Kashmir to Bhutan.
            </motion.p>

            {/* Trip Type Tabs + Search Widget */}
            <div className="mt-10 max-w-3xl">
              <div data-testid="hero-trip-tabs" className="flex flex-wrap gap-2 mb-3">
                {[
                  {k:"all", l:"All Trips"},
                  {k:"Adventure", l:"Adventure"},
                  {k:"Honeymoon", l:"Honeymoon"},
                  {k:"Group", l:"Group Tour"},
                  {k:"International", l:"International"},
                ].map(t => (
                  <button
                    key={t.k}
                    type="button"
                    data-testid={`trip-tab-${t.k}`}
                    onClick={() => setTripType(t.k)}
                    className={`px-4 py-2 rounded-full text-xs font-semibold ${
                      tripType === t.k
                        ? "btn-gold"
                        : "glass-dark text-white/80 hover:text-white"
                    }`}
                  >{t.l}</button>
                ))}
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const params = new URLSearchParams();
                  if (q) params.set("q", q);
                  if (tripType && tripType !== "all") params.set("category", tripType);
                  if (travelDate) params.set("date", travelDate);
                  if (pax) params.set("pax", pax);
                  window.location.href = `/packages?${params.toString()}`;
                }}
                data-testid="hero-search-form"
                className="glass-dark rounded-2xl p-2 grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_.6fr_auto] gap-2 items-stretch">
                <div className="flex items-center gap-2 px-4">
                  <Search className="w-4 h-4 text-[#D4AF37]"/>
                  <input
                    data-testid="hero-search-input"
                    value={q} onChange={(e)=>setQ(e.target.value)}
                    placeholder="Where to? Kashmir, Kerala..."
                    className="w-full py-3 bg-transparent text-white placeholder-white/60 focus:outline-none text-sm"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 border-l border-white/10">
                  <span className="text-[10px] uppercase tracking-widest text-white/60">Date</span>
                  <input
                    data-testid="hero-search-date"
                    type="date"
                    value={travelDate}
                    onChange={(e)=>setTravelDate(e.target.value)}
                    className="w-full py-3 bg-transparent text-white focus:outline-none text-sm [color-scheme:dark]"
                  />
                </div>
                <div className="flex items-center gap-2 px-4 border-l border-white/10">
                  <span className="text-[10px] uppercase tracking-widest text-white/60">Pax</span>
                  <input
                    data-testid="hero-search-pax"
                    type="number" min="1" max="20"
                    value={pax}
                    onChange={(e)=>setPax(+e.target.value)}
                    className="w-14 py-3 bg-transparent text-white focus:outline-none text-sm"
                  />
                </div>
                <button data-testid="hero-search-submit" className="px-6 py-3 rounded-xl btn-gold text-sm">Explore</button>
              </form>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-white/60">
                <span>Quick picks:</span>
                {["Kashmir", "Kerala", "Ladakh", "Bhutan", "Rajasthan"].map(t => (
                  <button key={t} type="button" onClick={() => { setQ(t); }}
                    className="hover:text-[#D4AF37] underline underline-offset-2">{t}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="absolute bottom-6 inset-x-0 z-10 text-center text-white/60 text-xs tracking-widest uppercase">Scroll to discover ↓</div>
      </section>

      {/* Deal Strip */}
      <DealStrip />

      {/* Stats */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {[
            { icon: Users, label:"Happy Travellers", val:`${stats.happy_travellers.toLocaleString('en-IN')}+`},
            { icon: Award, label:"Tours Completed", val:`${stats.tours_completed}+`},
            { icon: Compass, label:"Destinations", val:`${stats.destinations}+`},
            { icon: HeadphonesIcon, label:"Support", val: stats.support },
          ].map((s,i) => (
            <div key={i} data-testid={`stat-${i}`} className="rounded-3xl p-6 glass card-lift text-center">
              <s.icon className="w-8 h-8 mx-auto text-[#D4AF37]"/>
              <div className="mt-3 text-3xl font-bold text-blue-950 dark:text-white">{s.val}</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Destinations */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Signature Escapes</div>
              <h2 className="mt-3 text-4xl sm:text-5xl font-bold text-blue-950 dark:text-white">Featured Destinations</h2>
            </div>
            <Link to="/packages" className="hidden sm:block text-sm font-semibold text-[#D4AF37] hover:underline">View all →</Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featured.slice(0,6).map(p => <DestinationCard key={p.slug} p={p}/>)}
          </div>
        </div>
      </section>

      {/* Destination marquee */}
      <section className="py-8 bg-[#0B1120] overflow-hidden">
        <div className="flex marquee-track gap-10 whitespace-nowrap">
          {[...all, ...all].map((p, i) => (
            <Link key={i} to={`/packages/${p.slug}`} className="text-3xl font-[Playfair_Display] italic text-white/60 hover:text-[#D4AF37]">
              {p.location} <span className="mx-6 text-[#D4AF37]">✦</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Why choose us */}
      <section className="py-24 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Why Your Trips</div>
            <h2 className="mt-3 text-4xl sm:text-5xl font-bold text-blue-950 dark:text-white">Concierge-level care,<br/>every mile.</h2>
            <p className="mt-6 text-slate-600 dark:text-slate-300">From your first message to your last sunset — we obsess over every detail so your only job is to enjoy the journey.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {[
                {i:ShieldCheck, t:"Trusted since 2018", d:"5,000+ travellers, 4.9★ average rating"},
                {i:Award, t:"Handpicked stays", d:"4★ / 5★ properties personally vetted"},
                {i:HeadphonesIcon, t:"24×7 on-trip support", d:"A real human on WhatsApp, always"},
                {i:Compass, t:"Custom itineraries", d:"Every trip tailored to you"},
              ].map((x,i)=>(
                <div key={i} className="rounded-2xl p-5 bg-white dark:bg-slate-800 border border-slate-200/70 dark:border-white/10">
                  <x.i className="w-6 h-6 text-[#D4AF37]"/>
                  <div className="mt-3 font-semibold text-blue-950 dark:text-white">{x.t}</div>
                  <div className="text-sm text-slate-500 mt-1">{x.d}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <img src="https://images.pexels.com/photos/34053333/pexels-photo-34053333.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Luxury"
              className="rounded-3xl w-full h-[520px] object-cover shadow-2xl"/>
            <div className="absolute -bottom-6 -left-6 glass rounded-3xl p-5 max-w-xs">
              <div className="text-xs uppercase tracking-widest text-slate-500">Trusted by</div>
              <div className="text-3xl font-bold text-blue-950 dark:text-white">5,000+</div>
              <div className="text-sm text-slate-600 dark:text-slate-300">happy travellers across 16+ destinations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews slider */}
      <section className="py-24 bg-white dark:bg-slate-950">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.4em] text-[#D4AF37] font-semibold">Wanderlust Words</div>
            <h2 className="mt-3 text-4xl sm:text-5xl font-bold text-blue-950 dark:text-white">Loved by 5,000+ travellers</h2>
          </div>
          <Swiper modules={[Autoplay, Pagination]} spaceBetween={24}
            slidesPerView={1} breakpoints={{768:{slidesPerView:2}, 1024:{slidesPerView:3}}}
            autoplay={{delay:4000}} pagination={{clickable:true}}>
            {reviews.map((r) => (
              <SwiperSlide key={r.id}>
                <div data-testid={`review-${r.id}`} className="rounded-3xl p-8 bg-slate-50 dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 h-full">
                  <div className="flex gap-1 mb-4">{[...Array(r.rating)].map((_,i)=><Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]"/>)}</div>
                  <p className="text-slate-700 dark:text-slate-200 italic">"{r.comment}"</p>
                  <div className="mt-6 flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full btn-gold flex items-center justify-center font-bold">{r.name?.[0]}</div>
                    <div>
                      <div className="font-semibold text-blue-950 dark:text-white">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.location} · {r.package_slug}</div>
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-[#0B1120] relative overflow-hidden">
        <div className="absolute inset-0 opacity-40" style={{background:"radial-gradient(1200px 400px at 20% 20%, #D4AF37 0%, transparent 60%)"}}/>
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white">Your next great story starts with a message.</h2>
          <p className="mt-4 text-white/70 max-w-2xl mx-auto">Tell us where you're dreaming of — our concierge will craft a proposal within 24 hours.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="px-8 py-4 rounded-full btn-gold text-sm">Start Planning</Link>
            <a href="https://wa.me/919891782652" target="_blank" rel="noreferrer" className="px-8 py-4 rounded-full btn-outline-gold text-sm">Chat on WhatsApp</a>
          </div>
        </div>
      </section>
    </div>
  );
}
